'use client';

import type { IAnvändare, IOrganisation } from 'src/types/nya/common';

import React, { useMemo } from 'react';
import dayjs from 'dayjs';

import { AuthContext } from '../auth-context';
import { useDevRoller } from 'src/lib/dev-lage';

// ----------------------------------------------------------------------

/**
 * En uppsättning roller att granska appen genom.
 *
 * Rollnamnen måste finnas som nycklar i `ROLL_TILL_BEHÖRIGHETER`
 * (`src/layouts/nav-permissions.ts`). Skriv aldrig av dem på känsla — en
 * felstavad roll ger tyst en tom sidomeny i stället för ett fel.
 */
export type Persona = {
  nyckel: string;
  etikett: string;
  beskrivning: string;
  roller: string[];
};

export const PERSONAS: Persona[] = [
  {
    nyckel: 'medlem',
    etikett: 'Medlem',
    beskrivning: 'Vanlig boende. Den som vinner varje avvägning.',
    roller: ['medlem'],
  },
  {
    nyckel: 'hyresgast',
    etikett: 'Hyresgäst',
    beskrivning: 'Bor här men äger inte — får boende, inte medlem.',
    roller: ['hyresgäst'],
  },
  {
    nyckel: 'styrelseledamot',
    etikett: 'Styrelseledamot',
    beskrivning: 'Boende + medlem + styrelse + ekonomi.',
    roller: ['styrelseledamot'],
  },
  {
    nyckel: 'kassor',
    etikett: 'Kassör',
    beskrivning: 'Enbart ekonomi — ser inte Mitt boende alls.',
    roller: ['kassör'],
  },
  {
    nyckel: 'forvaltare',
    etikett: 'Förvaltare',
    beskrivning: 'Styrelse + ekonomi, men bor inte i föreningen.',
    roller: ['förvaltare'],
  },
  {
    nyckel: 'organisation_admin',
    etikett: 'Organisationsadmin',
    beskrivning: 'Allt utom superadmin och yrkesrollerna.',
    roller: ['organisation_admin'],
  },
  {
    nyckel: 'egenforetagare',
    etikett: 'Egenföretagare',
    beskrivning: 'Konsultytan + ekonomi.',
    roller: ['egenföretagare'],
  },
  {
    nyckel: 'arborist',
    etikett: 'Arborist',
    beskrivning: 'Enbart trädvårdsytorna.',
    roller: ['arborist'],
  },
  {
    nyckel: 'super_admin',
    etikett: 'Superadmin',
    beskrivning: 'Ser allt, inklusive Administration.',
    roller: ['super_admin'],
  },
];

const DEV_ADRESS = {
  gata: 'Rottnevägen',
  nr: '43',
  våning: '',
  postNummer: '128 48',
  ort: 'Bagarmossen',
  fullständigAdress: 'Rottnevägen 43, 128 48 Bagarmossen',
};

const DEV_ORGANISATION: IOrganisation = {
  id: 'dev-organisation',
  organisationsNamn: 'BRF Utvecklingsläget',
  organisationsNummer: '769999-0000',
  momsregisteringsnummer: 'SE769999000001',
  organisationsTyp: 'BOSTADSRÄTTSFÖRENING',
  adress: DEV_ADRESS,
  räkenskapsår: [],
};

type Props = {
  children: React.ReactNode;
};

/**
 * Inloggning utan konto, för granskning av gränssnittet.
 *
 * Ersätter Firebase-providern när `NEXT_PUBLIC_AUTH_MODE=dev`. Den fyller
 * `AuthContext` med en påhittad användare i stället för att prata med Firebase.
 *
 * Två detaljer är avgörande och lätta att missa:
 *
 * 1. `namn` **måste** vara satt. `filtreraNavigering` i
 *    `src/layouts/nav-permissions.ts` inleder med `if (!anvandare?.namn) return []`,
 *    så en användare utan namn ger en tom sidomeny — vilket ser ut som en
 *    trasig app snarare än som en saknad uppgift.
 * 2. `user.emailVerified` måste vara sant, annars skickar `AuthGuard`
 *    vidare till verifieringssidan.
 *
 * Rollerna går att byta i farten via {@link PERSONAS}, vilket är hela poängen:
 * en yta ska vara begriplig för varje behörighet som når den, och ett riktigt
 * konto har bara sina egna roller.
 */
export function DevAuthProvider({ children }: Props) {
    // Rollerna bor i modulstoren i `dev-lage`, inte i lokalt state — servern och
    // hydreringen får standardvärdet, klienten det sparade.
    const roller = useDevRoller();

    const anvandare: IAnvändare = useMemo(
        () => ({
            id: 'dev-anvandare',
            namn: {
                förnamn: 'Dev',
                efternamn: 'Devsson',
            },
            emailAdress: 'dev@example.invalid',
            personNummer: '19900101-0000',
            telefonNummer: '070-000 00 00',
            harGenomgåttOnboarding: true,
            roller: roller.map((namn) => ({
                aktiv: true,
                namn,
                skapadesPå: dayjs('2026-01-01'),
                orgId: DEV_ORGANISATION.id,
            })),
            adress: DEV_ADRESS,
            koppladeOrganisationer: [DEV_ORGANISATION],
            avatarUrl: '',
            valtSpråk: 'sv',
            identitetVerifierad: true,
        }),
        [roller]
    );

    const memoizedValue = useMemo(
        () => ({
            anvandare,
            organisationer: [DEV_ORGANISATION],
            user: {
                id: anvandare.id,
                uid: anvandare.id,
                displayName: `${anvandare.namn.förnamn} ${anvandare.namn.efternamn}`,
                email: anvandare.emailAdress,
                // Utan detta skickar AuthGuard vidare till /verifiera.
                emailVerified: true,
                photoURL: '',
                role: 'admin',
                accessToken: 'dev',
            },
            checkUserSession: async () => {},
            loading: false,
            error: null,
            authenticated: true,
            unauthenticated: false,
        }),
        [anvandare]
    );

    return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
